import { useEffect, useState } from "react";
import { LockedAutocomplete } from "@/components/LockedAutocomplete";
import type { AutocompleteOption } from "@/components/LockedAutocomplete";
import {
  isHeroMaterialOption,
  manualMaterialToOption,
  materialOptionKeepingImage,
  materialRecordToOption,
  timesheetMaterialSource,
} from "@/features/timesheetFlow/lib/timesheetMaterialSource";
import { getArtikelQuelle } from "@/lib/dokumente";
import type { HeroArticleSuggestion } from "@/lib/material";
import { resolveMaterialUnit } from "@/lib/materialUnits";
import { pricingFromDatabase } from "@/pages/dokumente/documentPricing";
import type {
  DocumentArticleApplyResult,
  DocumentArticleValue,
} from "@/pages/dokumente/DocumentArticleField.types";

export type { DocumentArticleApplyResult, DocumentArticleValue };

function plainInterneBeschreibung(value?: string | null): string {
  const raw = String(value || "").trim();
  if (!raw) return "";
  return raw
    .replace(/<br\s*\/?>/gi, "\n")
    .replace(/<\/p\s*>/gi, "\n")
    .replace(/<[^>]+>/g, "")
    .replace(/&nbsp;/gi, " ")
    .replace(/&amp;/gi, "&")
    .replace(/&lt;/gi, "<")
    .replace(/&gt;/gi, ">")
    .replace(/\n{3,}/g, "\n\n")
    .trim();
}

interface DocumentArticleFieldProps {
  value: DocumentArticleValue;
  disabled?: boolean;
  placeholder?: string;
  /** Nur Artikel mit Einheit Stunden / Std. aus der Datenbank vorschlagen. */
  stundenOnly?: boolean;
  onApply: (result: DocumentArticleApplyResult) => void;
}

export function DocumentArticleField({
  value,
  disabled,
  placeholder = "Artikel suchen oder frei eingeben …",
  stundenOnly = false,
  onApply,
}: DocumentArticleFieldProps) {
  const [option, setOption] = useState<AutocompleteOption<HeroArticleSuggestion> | null>(null);

  useEffect(() => {
    setOption((prev) =>
      materialOptionKeepingImage(
        prev,
        materialRecordToOption({
          text: value.bezeichnung,
          artikel_id: value.artikel_id,
          hero_nr: value.hero_nr,
          hero_unit: value.einheit,
          einheit: value.einheit,
        }),
      ),
    );
  }, [value.bezeichnung, value.artikel_id, value.hero_nr, value.einheit]);

  async function handleChange(opt: AutocompleteOption<HeroArticleSuggestion> | null) {
    setOption(opt);
    if (!opt) {
      onApply({
        bezeichnung: "",
        artikel_id: null,
        hero_nr: null,
        einheit: null,
        ist_freier_artikel: true,
      });
      return;
    }

    const article = opt.value;
    const articleUnit = article.einheit || article.hero_unit || null;
    const fallbackUnit = stundenOnly ? "Std." : "Stk";
    const base: DocumentArticleApplyResult = {
      bezeichnung: article.label,
      artikel_id: article.artikel_id,
      hero_nr: article.hero_nr,
      // Artikelstamm-Einheit hat Vorrang vor der bisherigen Positions-Einheit.
      einheit: resolveMaterialUnit(articleUnit || value.einheit || fallbackUnit, fallbackUnit),
      ist_freier_artikel: !article.artikel_id,
    };

    if (article.artikel_id) {
      try {
        const defaults = await getArtikelQuelle(article.artikel_id);
        const ek = defaults.einkaufspreis_netto ?? defaults.einzelpreis_netto;
        const vk = defaults.einzelpreis_netto;
        const pricing = pricingFromDatabase(ek, vk);
        const resolvedUnit = resolveMaterialUnit(
          defaults.einheit || articleUnit || base.einheit,
          fallbackUnit,
        );
        onApply({
          ...base,
          einzelpreis_netto: pricing.einzelpreis_netto,
          einkaufspreis_netto: pricing.einkaufspreis_netto,
          aufschlag_prozent: defaults.aufschlag_prozent ?? pricing.aufschlag_prozent,
          rabatt_prozent: defaults.rabatt_prozent,
          einheit: stundenOnly ? "Std." : resolvedUnit,
          hero_nr: defaults.hero_nr || base.hero_nr,
          bezeichnung: defaults.bezeichnung || base.bezeichnung,
          ist_freier_artikel: false,
          preis_quelle: defaults.preis_quelle,
          preis_abgelaufen: defaults.preis_abgelaufen,
          orientierungspreis_netto: defaults.orientierungspreis_netto ?? null,
          preis_gueltig_bis: defaults.preis_gueltig_bis ?? null,
          interne_beschreibung: plainInterneBeschreibung(defaults.interne_beschreibung),
        });
        return;
      } catch {
        // Freitext-Fallback
      }
    }

    onApply(stundenOnly ? { ...base, einheit: "Std." } : base);
  }

  return (
    <div className="gd-article-field [&_.eh-input]:text-sm">
      <LockedAutocomplete<HeroArticleSuggestion>
        placeholder={
          stundenOnly ? "Stunden-Artikel suchen oder frei eingeben …" : placeholder
        }
        value={option}
        minChars={2}
        allowFreeText
        allowEditLocked={(opt) => !isHeroMaterialOption(opt)}
        createFreeTextOption={manualMaterialToOption}
        loadOptions={(q) => timesheetMaterialSource(q, { stundenOnly })}
        onChange={(opt) => void handleChange(opt)}
        disabled={disabled}
      />
    </div>
  );
}
